import { useMemo } from "react";
import { Link } from "react-router-dom";

import { ROUTES, toConcept, toGame } from "../../../common/navigation";
import { sourceForGame } from "../../../data/dataSources";
import useAuth from "../../../hooks/useAuth";
import useLearning from "../../../hooks/useLearning";
import useSchedule from "../../../hooks/useSchedule";
import {
  TOTAL_CONCEPTS,
  getNextConcept,
  getConcepts,
} from "../../../services/learning/conceptService";
import { readGameStatus } from "../../../utils/gameStatus";
import { gameStatusLabel } from "../../../utils/situation";
import EmptyState from "../../common/EmptyState/EmptyState";
import ErrorState from "../../common/ErrorState/ErrorState";
import GameCard from "../../common/GameCard/GameCard";
import IQProgressBar from "../../common/IQProgressBar/IQProgressBar";
import LoadingState from "../../common/LoadingState/LoadingState";
import PageContainer from "../../common/PageContainer/PageContainer";
import StatCard from "../../common/StatCard/StatCard";
import GameMeta from "../../common/GameMeta/GameMeta";
import Matchup from "../../common/Matchup/Matchup";
import SourceBadge from "../../common/SourceBadge/SourceBadge";
import StatusBadge from "../../common/StatusBadge/StatusBadge";
import { gameWashStyle } from "../../../utils/teamWash";
import styles from "./Home.module.css";

/**
 * Home answers exactly one question: "what can I do right now?" (PRD §11).
 * A hero with the next thing to watch, what to learn next, and progress.
 */
const Home = () => {
  const { profile, isGuest } = useAuth();
  const { learnedIds, learnedCount } = useLearning();
  const { games, loading, error, retry } = useSchedule({ limit: 6 });

  const nextGame = games[0] ?? null;
  const nextGameStatus = readGameStatus(nextGame);
  const upcoming = games.slice(1, 4);

  const nextConcept = useMemo(() => getNextConcept(learnedIds), [learnedIds]);
  const recentlyLearned = useMemo(
    () => getConcepts(learnedIds.slice(-3)),
    [learnedIds]
  );

  return (
    <PageContainer>
      <header className="mb-3">
        <p className="app-eyebrow mb-1">
          {isGuest ? "Playing as a guest" : "Welcome back"}
        </p>
        <h1 className="h3 mb-0">Hey {profile?.alias ?? "there"} 👋</h1>
      </header>

      {/* ---------------------------------------------- hero */}
      {loading && <LoadingState title="Looking for a game…" compact />}

      {!loading && error && (
        <ErrorState
          title="We couldn't load the schedule."
          onRetry={retry}
          detail={error?.message}
          className="mb-4"
        />
      )}

      {!loading && !error && !nextGame && (
        <EmptyState
          title="No games right now."
          message="Nothing is scheduled. In the meantime, the Learn tab works any day of the week."
          action={
            <Link to={ROUTES.LEARN} className="btn btn-primary">
              Learn something
            </Link>
          }
          className="mb-4"
        />
      )}

      {!loading && !error && nextGame && (
        <section
          className={`${styles.hero} app-team-wash`}
          style={gameWashStyle(nextGame)}
          aria-label="Next game"
        >
          {/* Same shape as a schedule card, one size up: badges, the
              matchup, when it is, one thing to do. */}
          <div className={styles.heroBadges}>
            <span className={styles.heroBadgesLeft}>
              {/* "Next game" is a claim, and it is wrong for a game that has
                  finished or been called off. readGameStatus is shared with
                  the schedule card precisely so the two cannot disagree, and
                  this hero was the one surface still ignoring half of it. */}
              <StatusBadge tone="accent">
                {nextGameStatus.final
                  ? "Last game"
                  : nextGameStatus.offSchedule
                    ? "Scheduled game"
                    : "Next game"}
              </StatusBadge>
              {nextGameStatus.live && (
                <StatusBadge tone="live" pulse>
                  Live
                </StatusBadge>
              )}
              {nextGameStatus.final && (
                <StatusBadge tone="final">Final</StatusBadge>
              )}
              {nextGameStatus.offSchedule && (
                <StatusBadge tone="warning">
                  {gameStatusLabel(nextGameStatus.status)}
                </StatusBadge>
              )}
            </span>

            {nextGame.isSimulated ? (
              <StatusBadge tone="warning" icon="fa-solid fa-flask">
                Practice
              </StatusBadge>
            ) : (
              <SourceBadge
                source={sourceForGame(nextGame)}
                size="sm"
                showName={false}
              />
            )}
          </div>

          <Matchup
            as="h2"
            game={nextGame}
            size="lg"
            className={styles.heroMatchup}
          />

          <GameMeta game={nextGame} className={styles.heroMeta} />

          <Link
            to={toGame(nextGame.gameId)}
            className={`btn btn-primary btn-lg ${styles.heroButton}`}
          >
            {nextGameStatus.live
              ? "Follow it live"
              : nextGameStatus.final
                ? "See the recap"
                : nextGameStatus.offSchedule
                  ? "See what we know"
                  : "Get ready"}
          </Link>
        </section>
      )}

      {/* ---------------------------------------------- continue learning */}
      <section className={styles.section} aria-label="Continue learning">
        <h2 className="h6 mb-2">Continue learning</h2>

        <div className={styles.learnCard}>
          {recentlyLearned.length > 0 ? (
            <p className={styles.learnRecap}>
              You&rsquo;ve learned{" "}
              <strong>
                {recentlyLearned.map((c) => c.title).join(", ")}
              </strong>
              {learnedCount > recentlyLearned.length
                ? ` and ${learnedCount - recentlyLearned.length} more.`
                : "."}
            </p>
          ) : (
            <p className={styles.learnRecap}>
              You haven&rsquo;t learned anything yet, which is exactly why
              you&rsquo;re here.
            </p>
          )}

          {nextConcept ? (
            <>
              <p className={styles.learnNext}>
                Next up: <strong>{nextConcept.title}</strong>
              </p>
              <p className={styles.learnShort}>{nextConcept.shortExplanation}</p>
              <Link
                to={toConcept(nextConcept.conceptId)}
                className="btn btn-outline-primary"
              >
                <span aria-hidden="true">{nextConcept.emoji}</span> Learn it
              </Link>
            </>
          ) : (
            <p className={styles.learnNext}>
              You&rsquo;ve read all {TOTAL_CONCEPTS} of them. At this point you
              do know football.
            </p>
          )}
        </div>
      </section>

      {/* ---------------------------------------------- upcoming games */}
      {upcoming.length > 0 && (
        <section className={styles.section} aria-label="Upcoming games">
          <div className="d-flex align-items-baseline justify-content-between mb-2">
            <h2 className="h6 mb-0">Coming up</h2>
            <Link to={ROUTES.GAMES} className="small">
              See all
            </Link>
          </div>

          <ul className={styles.gameList}>
            {upcoming.map((game) => (
              <li key={game.gameId}>
                <GameCard game={game} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---------------------------------------------- progress */}
      <section className={styles.section} aria-label="Your progress">
        <h2 className="h6 mb-2">Your progress</h2>

        <div className="app-surface p-3 mb-3">
          <IQProgressBar iq={profile?.footballIQ ?? 0} />
        </div>

        <div className="row g-3">
          <div className="col-6 col-lg-4">
            <StatCard
              label="Concepts learned"
              value={learnedCount}
              hint={`out of ${TOTAL_CONCEPTS}`}
              icon="fa-solid fa-lightbulb"
            />
          </div>
          <div className="col-6 col-lg-4">
            <StatCard
              label="Games watched"
              value={profile?.gamesWatched ?? 0}
              icon="fa-solid fa-tv"
              tone="accent"
            />
          </div>
          <div className="col-6 col-lg-4">
            <StatCard
              label="Predictions correct"
              value={profile?.predictionsCorrect ?? 0}
              icon="fa-solid fa-bullseye"
            />
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default Home;
